/**
 * PATH: packages/ui/src/kanban/components/KanbanCardDuePills.tsx
 * FILE_ID: UI-KANBAN-CARD-DUE-PILLS-001
 * PURPOSE: The card footer's NEXT-ACTION date pills — the due-date pill
 *   (calendar glyph when upcoming, ⚠ when overdue) and the snooze pill.
 * WHY: Operator ask 2026-06-14 ("show the date on the card for the next
 *   action"): the due date WAS already painted, but a non-overdue date showed
 *   as a bare "MM-DD" with no icon/label — indistinguishable from any other
 *   number on a dense triage card. This makes it unmistakably read as the
 *   next-action deadline (leading calendar glyph + spelled-out tooltip +
 *   aria-label) while keeping the compact MM-DD glanceable. Extracted from
 *   KanbanCardFooter.tsx so that file stays under the 300-line working limit.
 * FLOW:
 *   ┌──────────────────┐   ┌─────────────────────┐   ┌──────────────────┐
 *   │ card.due_date /  │ → │ slice MM-DD + pure  │ → │ icon + MM-DD pill │
 *   │ card.snoozed_*   │   │ title/aria helpers  │   │ + readable tip    │
 *   └──────────────────┘   └─────────────────────┘   └──────────────────┘
 * RELATED:
 *   - ./KanbanCardFooter.tsx: renders this in the triage icon strip.
 *   - ./kanbanCardVisuals.ts: formatDuePillTitle / formatSnoozePillTitle.
 */
import { AlertTriangle, CalendarClock, Clock, Target } from 'lucide-react'

import type { Card as KanbanCardModel } from '../types'
import {
  formatActionPillTitle,
  formatDuePillTitle,
  formatSnoozePillTitle,
} from './kanbanCardVisuals'
import { formatDmMonthShort } from '../utils/localDate'
import { KanbanCardDateChip } from './KanbanCardDateChip'

// Matches the triage-strip glyphs in KanbanCardFooter: small at rest, zoom on
// hover of that specific icon (operator 2026-06-23).
const DUE_ICON = 'inline w-3 h-3 origin-center transition-transform duration-150 hover:scale-[1.6]'

/**
 * FEAT-2026-07-06-KANBAN-PREV-DATES — which previous date (if any) to subtend
 * under a date pill. Pure I→O so the gate is unit-testable: paints only when
 * the server-denormalised previous value exists AND differs from the current
 * value (a no-op PATCH echo must not draw a strikethrough of itself).
 */
export function previousDateLine(
  current: string | null | undefined,
  previous: string | null | undefined,
): string | null {
  if (!previous) return null
  const prev = previous.slice(0, 10)
  if (current && current.slice(0, 10) === prev) return null
  return prev
}

/** The struck-through red "what it was before" line under a date pill
 *  (operator 2026-07-06: "The old date subtends with hilighted red ... to
 *  see visibly what it was before"). Self-gating via previousDateLine. */
function PreviousDateSubline({
  current,
  previous,
}: {
  current: string | null | undefined
  previous: string | null | undefined
}) {
  const prev = previousDateLine(current, previous)
  if (!prev) return null
  return (
    <span
      className="block text-[9px] leading-tight text-red-500 line-through dark:text-red-400"
      title={`Previously ${formatDmMonthShort(prev)}`}
      aria-label={`Previously ${formatDmMonthShort(prev)}`}
    >
      {formatDmMonthShort(prev)}
    </span>
  )
}

export interface KanbanCardDuePillsProps {
  card: KanbanCardModel
  /** Pre-computed in the card root (local-calendar compare): due today is
   *  NOT overdue. Passed in so the pill and the card ring agree. */
  overdue: boolean
  /** Card design v2 — the expanded face renders the action date on the
   *  ACTION LINE (KanbanCardActionLine), so the footer must not paint it
   *  twice. When true the pills skip the action pill and fall through to
   *  the due pill (which the action line deliberately does NOT carry). */
  hideActionPill?: boolean
  /** Card-face recipe: omit the due pill; card.due_date stays on the card. */
  hideDuePill?: boolean
  hidePreviousDue?: boolean
  hidePreviousAction?: boolean
  hideSnooze?: boolean
  /** Card design v2 — click-to-edit. Same signatures as the quick-action
   *  bundle; omitted → pills stay static (read-only hosts unchanged). */
  onSetActionDate?: (
    card: KanbanCardModel,
    actionDate: string | null,
  ) => Promise<unknown> | void
  onSetDueDate?: (
    card: KanbanCardModel,
    dueDate: string | null,
  ) => Promise<unknown> | void
}

/**
 * Renders the due-date and snooze pills. Self-gating: each pill only paints
 * when its backing field is set, so a card with neither produces nothing
 * (the parent strip keeps its existing "render only when there's signal"
 * contract).
 */
export function KanbanCardDuePills({
  card,
  overdue,
  hideActionPill = false,
  hideDuePill = false,
  hidePreviousDue = false,
  hidePreviousAction = false,
  hideSnooze = false,
  onSetActionDate,
  onSetDueDate,
}: KanbanCardDuePillsProps) {
  // FEAT-2026-06-29-KANBAN-ACTION-DATE — the operator's PRIMARY, hand-
  // authored "next action" date takes the card-face date slot when set,
  // falling back to due_date otherwise (operator: "Action Date ... should
  // also be the default on the date allocation"). The action pill is
  // NEUTRAL: overdue (red) deliberately stays a due_date concept (so does
  // the card ring, computed in KanbanCard from due_date) — we don't paint a
  // DIFFERENT date red. action_note is drawer-only (excluded from the lean
  // board payload), so the tile tooltip is just "Action <date>".
  //
  // FEAT-2026-07-06-KANBAN-PREV-DATES — when the date was recently moved
  // (drag between time lanes / drawer edit), the server-denormalised
  // previous value renders as a struck-through red line UNDER the pill so
  // the operator sees "what it was before" at a glance.
  // Card design v2 — when the expanded face's ACTION LINE owns the action
  // date, the footer treats the card as action-less and falls through to
  // the due pill (no double paint).
  const actionDate = hideActionPill ? null : (card.action_date ?? null)
  return (
    <>
      {actionDate ? (
        <KanbanCardDateChip
          value={actionDate}
          ariaLabel={formatActionPillTitle(actionDate, card.action_note)}
          onCommit={
            onSetActionDate ? (iso) => onSetActionDate(card, iso) : undefined
          }
        >
          <span
            className="inline-flex flex-col items-start"
            title={formatActionPillTitle(actionDate, card.action_note)}
            aria-label={formatActionPillTitle(actionDate, card.action_note)}
          >
            <span className="inline-flex items-center gap-0.5">
              <Target className={DUE_ICON} aria-hidden />
              {formatDmMonthShort(actionDate)}
            </span>
            <PreviousDateSubline
              current={actionDate}
              previous={hidePreviousAction ? null : card.previous_action_date}
            />
          </span>
        </KanbanCardDateChip>
      ) : (
        !hideDuePill && card.due_date && (
          <KanbanCardDateChip
            value={card.due_date}
            ariaLabel={formatDuePillTitle(card.due_date, overdue)}
            onCommit={
              onSetDueDate ? (iso) => onSetDueDate(card, iso) : undefined
            }
          >
            <span
              className={[
                'inline-flex flex-col items-start',
                overdue ? 'text-red-600 dark:text-red-400 font-medium' : '',
              ].join(' ')}
              // Spell out what the bare MM-DD means so it reads as the
              // next-action deadline, not an anonymous number.
              title={formatDuePillTitle(card.due_date, overdue)}
              aria-label={formatDuePillTitle(card.due_date, overdue)}
            >
              <span className="inline-flex items-center gap-0.5">
                {overdue ? (
                  <AlertTriangle className={DUE_ICON} aria-hidden />
                ) : (
                  // Calendar glyph on an upcoming due date — the missing affordance
                  // that made a non-overdue due date indistinguishable from any
                  // other number on the card.
                  <CalendarClock className={DUE_ICON} aria-hidden />
                )}
                {formatDmMonthShort(card.due_date)}
              </span>
              <PreviousDateSubline
                current={card.due_date}
                previous={hidePreviousDue ? null : card.previous_due_date}
              />
            </span>
          </KanbanCardDateChip>
        )
      )}
      {!hideSnooze && card.status === 'SNOOZED' && card.snoozed_until && (
        <span
          className="inline-flex items-center gap-0.5"
          title={formatSnoozePillTitle(card.snoozed_until)}
          aria-label={formatSnoozePillTitle(card.snoozed_until)}
        >
          <Clock className={DUE_ICON} aria-hidden />
          {formatDmMonthShort(card.snoozed_until)}
        </span>
      )}
    </>
  )
}
